import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from '../hooks/useWallet';
import { useAccess } from '../hooks/useAccess';
import { SiHiveBlockchain } from "react-icons/si";

export const LoginMetamask = () => {
  // The hook now provides the full authentication state and functions
  const { login, jwtToken, isLoading, error, account, logout, connectionStatus, isConnected, isInitializing, network } = useWallet();
  // Wallet auth may be closed (KAT-230) while existing sessions wind down.
  const { login: loginOpen } = useAccess();
  // const navigate = useNavigate();
  const prevJwtRef = React.useRef(null);

  const navigate = useNavigate();

  // Listen for wallet switching events from SignedWalletProvider
  const handleLoginSuccess = useCallback((event) => {
    if (event.detail?.shouldNavigateToDashboard) {
      console.log("Login successful (from event), navigating to dashboard");
      navigate('/dashboard');
    }
  }, [navigate]);

  useEffect(() => {
    window.addEventListener('loginSuccess', handleLoginSuccess);
    return () => {
      window.removeEventListener('loginSuccess', handleLoginSuccess);
    };
  }, [handleLoginSuccess]);

  // This is the event handler for the login button click
  const handleLogin = async () => {
    // Call the login function and wait for its boolean result
    const loginSuccessful = await login();

    // If login failed, the provider has already set an error message.
    // Navigation is handled by the loginSuccess event listener.
  };


  // Once wallet auth is closed there is nothing to offer a signed-out visitor —
  // render nothing rather than an empty wrapper that still takes header space.
  const hasLiveSession = Boolean(jwtToken && account);
  if (!loginOpen && !isInitializing && !hasLiveSession && !error) {
    return null;
  }

  return (
    <div className="mb-6 flex flex-col justify-center items-center">
      {/* Show loading spinner while initializing */}
      {isInitializing ? (
        <div className="flex items-center justify-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          <span className="text-gray-600">Checking connection...</span>
        </div>
      ) : jwtToken && account ? (
        /* Fully connected: JWT + wallet restored */
        <div className="flex flex-col items-center justify-center gap-2 p-1 rounded-xl bg-zinc-800 text-white shadow">
          <div className="flex flex-row gap-3 p-1">
            <p className="flex items-center gap-2 text-sm font-medium text-emerald-400">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="font-mono text-xs">{`${account.substring(0, 8)}...`}</span>
            </p>
            {network && (
              <div className="flex items-center gap-1.5 text-xs font-medium text-blue-400 bg-blue-900/40 border border-blue-700/50 rounded-md px-2 py-1">
                <SiHiveBlockchain className="text-blue-400" size={14} />
                <span>{network.name && network.name !== 'unknown' ? network.name : `Chain ${Number(network.chainId)}`}</span>
              </div>
            )}
            <button
              onClick={logout}
              className="rounded-lg bg-red-500 px-3 py-1.5 text-sm font-semibold text-white shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : !loginOpen ? null : jwtToken && !account ? (
        /* Authenticated but wallet not restored — prompt reconnect, not fresh login */
        <button
          onClick={handleLogin}
          disabled={isLoading}
          className="bg-zinc-700 hover:bg-zinc-600 text-white px-4 py-2 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
        >
          {isLoading ? "Reconnecting..." : "Reconnect Wallet"}
        </button>
      ) : (
        /* No session — fresh login */
        <button
          onClick={handleLogin}
          disabled={isLoading}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {isLoading ? "Logging In..." : "Connect Wallet"}
        </button>
      )}

      {/* Display any errors that occur during the login process */}
      {error && (
        <div className="mt-4 p-2 text-red-700 bg-red-100 border border-red-400 rounded">
          {error}
        </div>
      )}
    </div>
  );
};